import _ from "lodash";
import React from "react";
import classes from "./NodeTreeEditor.scss";
import NodeTreeView from "./NodeTreeView";
import NodeTreeViewV2 from "./NodeTreeViewV2";
import OutLine from "./OutLine";
import { connect } from "react-redux";
import {
  updateSearchFilters,
  initNodeList,
  initRootNode,
  updateEditNodeList,
  handleBenchmarkInBulk,
} from "MultiLevelNodeEditor/modules/MultiLevelNodeEditorModule";

import {
  orderNodes,
  nodeFilterByGrades,
  buildSearchFlatTree,
  scrollToWithAnimation,
} from "Utils";
import { compose } from "lib/apolloClient";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import DialogueBox from "UIComponents/DialogueBox";
import I18nHOC from "UIComponents/I18nHOC";
import CoachMarks from "AppComponents/CoachMarks";
import ACLStore from "lib/aclStore";
import { ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION } from "Constants/permissionConstants";

const DIALOGS_INFO = {
  DELETE: {
    title: t => t("common:delete"),
    message: t => t("common:action_msg", { action: t("common:delete") }),
    confirmButtonText: t => t("common:delete_agree"),
    cancelButtonText: t => t("common:agree_cancel"),
  },
  SAVE: {
    title: t => t("common:save"),
    message: t => t("common:action_msg", { action: t("common:save") }),
    confirmButtonText: t =>
      t("common:action_agree", { action: t("common:save") }),
    cancelButtonText: t => t("common:cancel"),
  },
  DISCARD: {
    title: t => t("common:discard_changes"),
    message: t => t("common:exit_without_save_prompt"),
    confirmButtonText: t => t("common:save_exit"),
    cancelButtonText: t => t("common:discard_exit"),
  },
};

class NodeTreeEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedNodeId: "",
      taggedNodeIds: props.value ? props.value : [],
      currentDialogAction: "",
      isCoachMarksRunning: false,
      recommendedPhaseId: "",
      sideNavSelectedNodeId: "",
      shouldShowSideNav: false,
      filteredObjectiveNodes: [],
      objectiveOutlineNodes: [],
      recommendedObjectiveNode: {},
    };
    this.isAnimatingScroll = false;
    this.currentDialogItem = {};
    this.outlineRefs = {};
    this.nodesRef = {};

    props.updateRef(this);
  }

  getTaggedIds = () => {
    return _.uniq(this.state.taggedNodeIds);
  };

  // It is used in Levels.js using ref of this comp
  getModalState = () => {
    const { filters } = this.props;

    return {
      searchText: filters.searchText,
      selectedEntities: _.uniq(this.state.taggedNodeIds),
    };
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { taggedNodeIds: oldTaggedNodeIds } = prevState;
    const { taggedNodeIds, shouldShowSideNav } = this.state;
    const {
      updateValueLocally,
      updateSelectedCount,
      isDsComponent,
      rootNode,
      filteredNodes,
    } = this.props;
    const { value } = this.props; // here value is checked field which is reveived from props
    const { value: oldValue } = prevProps;
    // if (oldTaggedNodeIds != value) {
    //   this.setState({
    //     taggedNodeIds: value
    //   });
    // }
    if (!_.isEqual(oldValue, value)) {
      if (shouldShowSideNav) {
        const selectedObjectiveIds = rootNode?.selectedObjectiveIds;
        const selectedObjectivesNodes = _.filter(filteredNodes, node =>
          _.includes(selectedObjectiveIds, node.id)
        );
        const objectiveRootNodesWithCount = _.map(
          selectedObjectivesNodes,
          node => {
            const currentObjectiveNodesId = _.map(
              this.getSelectedPhaseNodes({
                nodeId: node?.id,
              }),
              node => node.id
            );
            const selectedValue = _.filter(value, id =>
              _.includes(currentObjectiveNodesId, id)
            );
            return {
              ...node,
              count: selectedValue.length,
            };
          }
        );
        this.setState({
          objectiveOutlineNodes: objectiveRootNodesWithCount,
        });
      }
    }
    if (taggedNodeIds != oldTaggedNodeIds) {
      if (updateValueLocally) {
        updateValueLocally(taggedNodeIds);
      }
      if (isDsComponent && updateSelectedCount) {
        updateSelectedCount(taggedNodeIds?.length);
      }
    }
  };

  onCheckBoxClick = ({ id }) => {
    const { taggedNodeIds, shouldShowSideNav } = this.state;
    const { onSubmitClick, shouldSaveOnUpdate } = this.props;
    let updatedToggleNodeIds = [];
    const valuesToAdd = [];
    const valuesToRemove = [];
    if (_.includes(taggedNodeIds, id)) {
      valuesToRemove.push(id);
      updatedToggleNodeIds = _.filter(taggedNodeIds, nodeId => nodeId != id);
    } else {
      valuesToAdd.push(id);
      updatedToggleNodeIds = [...taggedNodeIds, id];
    }
    this.setState({ taggedNodeIds: updatedToggleNodeIds });

    if (shouldSaveOnUpdate) {
      setTimeout(() => {
        onSubmitClick({
          valuesToAdd,
          valuesToRemove,
          shouldCloseModal: false,
        });
      });
    }
  };

  updateTaggedNodes = value => {
    this.setState({ taggedNodeIds: value });
  };

  getSelectedPhaseNodes = ({ currentPhaseNodes = [], nodeId }) => {
    const { filteredNodes } = this.props;
    _.forEach(filteredNodes, node => {
      if (node.id == nodeId) {
        currentPhaseNodes.push(node);
      }
      if (!_.isEmpty(node?.children) && node.id == nodeId) {
        _.forEach(node.children, childNode => {
          this.getSelectedPhaseNodes({
            currentPhaseNodes: currentPhaseNodes,
            nodeId: childNode,
          });
        });
      }
    });
    return currentPhaseNodes;
  };

  componentDidMount = () => {
    const {
      initNodeList,
      filteredNodes,
      mode,
      rootNode,
      initRootNode,
      searchText = "",
      nodeType,
      showOutline,
      value,
      subtypes,
    } = this.props;

    if (
      _.isEqual(nodeType, "MYP_OBJECTIVES") &&
      ACLStore.can(ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION) &&
      !_.isEqual(rootNode?.id, "INTERDISCIPLINARY_CRITERIA") &&
      !subtypes
    ) {
      const selectedObjectiveIds = rootNode?.selectedObjectiveIds;
      const selectedObjectivesNodes = _.filter(filteredNodes, node =>
        _.includes(selectedObjectiveIds, node.id)
      );
      const objectiveRootNodesWithCount = _.map(
        selectedObjectivesNodes,
        node => {
          const currentObjectiveNodesId = _.map(
            this.getSelectedPhaseNodes({
              nodeId: node?.id,
            }),
            node => node.id
          );
          const selectedValue = _.filter(value, id =>
            _.includes(currentObjectiveNodesId, id)
          );
          return {
            ...node,
            count: selectedValue.length,
          };
        }
      );
      this.setState({
        shouldShowSideNav: showOutline,
        recommendedPhaseId: rootNode?.recommendedPhase,
        sideNavSelectedNodeId: rootNode?.recommendedPhase,
        filteredObjectiveNodes: this.getSelectedPhaseNodes({
          nodeId: rootNode?.recommendedPhase,
        }),
        objectiveOutlineNodes: objectiveRootNodesWithCount,
        recommendedObjectiveNode: _.find(filteredNodes, {
          id: rootNode?.recommendedPhase,
        }),
      });
    }
    this.onFilterChanged({ searchText });

    if (mode == "edit") {
      initRootNode(rootNode);
      initNodeList({ nodes: filteredNodes });
    }
  };

  onConceptualUnderStandingCheckBoxClick = () => {
    const { editRootNode, initRootNode } = this.props;
    const isExits = _.find(
      _.get(editRootNode, "snsChildren", []),
      item => item == "CONCEPTUAL_UNDERSTANDING"
    );
    let snsChildren = ["CONCEPTUAL_UNDERSTANDING", "LEARNING_OUTCOME"];
    if (isExits) {
      snsChildren = ["LEARNING_OUTCOME"];
    }
    initRootNode({ ...editRootNode, snsChildren });
  };

  onChangeOutline = ({ selectedNodeId, forcePosition }) => {
    const { shouldShowSideNav } = this.state;
    if (selectedNodeId) {
      this.setState({ selectedNodeId });
    }
    if (shouldShowSideNav && selectedNodeId) {
      this.setState({
        sideNavSelectedNodeId: selectedNodeId,
        filteredObjectiveNodes: this.getSelectedPhaseNodes({
          nodeId: selectedNodeId,
        }),
      });
    }
    if (!shouldShowSideNav && forcePosition && this.nodesRef[selectedNodeId]) {
      const nodeTop = _.get(this.nodesRef[selectedNodeId], "offsetTop", 0);
      this.scrollToWithAnimation(
        this.scrollContainer,
        nodeTop - (this.scrollContainer.offsetTop + 40),
        forcePosition
      );
    }
  };

  scrollToWithAnimation = (scrollComp, value, forcePosition) => {
    if (this.isAnimatingScroll && !forcePosition) {
      return;
    }

    scrollToWithAnimation(scrollComp, value, 300);
    if (forcePosition) {
      this.isAnimatingScroll = true;
      setTimeout(() => {
        this.isAnimatingScroll = false;
      }, 300);
    }
  };

  checkScroll = ({ selectedNodeId }) => {
    const { mode } = this.props;
    if (this.outLineScrollRef && this.outlineRefs[selectedNodeId]) {
      const outLineNode = this.outlineRefs[
        selectedNodeId
      ].getBoundingClientRect();

      const scrollNode = this.outLineScrollRef.getBoundingClientRect();

      const relativeTop = outLineNode.bottom + 32 - scrollNode.top;

      if (this.outLineScrollRef.clientHeight < relativeTop) {
        this.scrollToWithAnimation(
          this.outLineScrollRef,
          this.outLineScrollRef.scrollTop +
            (relativeTop - this.outLineScrollRef.clientHeight)
        );
      } else if (outLineNode.top - 120 <= 0) {
        this.scrollToWithAnimation(
          this.outLineScrollRef,
          this.outLineScrollRef.scrollTop - Math.abs(outLineNode.top - 120)
        );
      }
    }
  };

  onFilterChanged = params => {
    const { updateSearchFilters } = this.props;
    updateSearchFilters(params);
  };

  onActionClick = ({ index, action, params }) => {
    this.currentDialogItem = { params, index };
    this.setState({ currentDialogAction: action });
  };

  OnToggleDialogueClick = ({ isCancel } = {}) => {
    const { currentDialogAction } = this.state;
    const { onBackConfirmClick } = this.props;
    switch (currentDialogAction) {
      case "DISCARD":
        if (!isCancel) {
          onBackConfirmClick();
        }
        break;
    }
    this.setState({ currentDialogAction: "" });
    this.currentDialogItem = {};
  };

  onDialogConfirmClick = async () => {
    const { currentDialogAction } = this.state;
    const {
      updateEditNodeList,
      handleBenchmarkInBulk,
      plannerElementSetConfig,
      onChangeMode,
    } = this.props;
    const { params, index } = this.currentDialogItem;
    switch (currentDialogAction) {
      case "SAVE":
      case "DISCARD": {
        const isSuccess = await handleBenchmarkInBulk({
          plannerElementSetConfig,
        });
        if (isSuccess) {
          onChangeMode("view");
        }
        break;
      }

      default:
        updateEditNodeList({
          action: currentDialogAction,
          index,
          params,
        });
    }
  };

  updateOutlineRef = ({ id, ref }) => {
    this.outlineRefs[id] = ref;
  };

  updateNodesRef = ({ id, ref }) => {
    this.nodesRef[id] = ref;
  };

  updateScrollContainerRef = ref => {
    this.scrollContainer = ref;
  };

  handleScroll = () => {
    if (this.scrollContainer && !this.isAnimatingScroll) {
      let currentNode = null;

      _.forEach(this.nodesRef, (node, id) => {
        if (node) {
          const { top: nodeTop } = node.getBoundingClientRect() || {};

          if (
            nodeTop <= this.scrollContainer.clientHeight * 0.75 &&
            nodeTop >= 0
          ) {
            currentNode = id;
          }
        }
      });
      if (currentNode) {
        this.onChangeOutline({ selectedNodeId: currentNode });
        setTimeout(() => {
          this.checkScroll({ selectedNodeId: currentNode });
        }, 50);
      }
    }
  };

  onStepChanged = ({ isDone, isSkipped }) => {
    if (isDone || isSkipped) {
      this.setState({ isCoachMarksRunning: false });
    } else if (!this.state.isCoachMarksRunning) {
      this.setState({ isCoachMarksRunning: true });
    }
  };

  updateOutlineScrollRef = ({ ref }) => {
    this.outLineScrollRef = ref;
  };

  getSearchPlaceHolder = () => {
    const { nodeType, isDsComponent } = this.props;

    if (_.endsWith(nodeType, "ATL"))
      return isDsComponent
        ? "common:search_approaches_to_learning"
        : "academicSetup:search_for_skill";
    if (_.endsWith(nodeType, "OBJECTIVES"))
      return isDsComponent
        ? "common:search_objectives"
        : "academicSetup:search_for_yearly_objective";

    return "academicSetup:search_for_standard";
  };

  render() {
    const {
      nodes,
      mode,
      filteredNodes,
      filters,
      isTagging,
      showInsights,
      updateGradeFilters,
      gradeFilters,
      rootOptionsGrades,
      editNodes,
      updateEditNodeList,
      isLoading,
      rootNode,
      editRootNode,
      theme,
      uptoDepth,
      showSelectedCount,
      value,
      t,
      nodeParentType,
      startDepth,
      showOutline,
      showSearchHeader,
      multiSelectConfig,
      nodeRenderType,
      insightsData,
      nodeType,
      showGrade,
      showParentInsightsCount,
      isDsComponent = false,
      updateSelectedCount,
      openedFrom,
      updateIsDisabled,
      showGradeFilterExplicitly,
      dsVersion,
      testId,
    } = this.props;
    const {
      selectedNodeId,
      taggedNodeIds,
      currentDialogAction,
      isCoachMarksRunning,
      shouldShowSideNav,
      sideNavSelectedNodeId,
      filteredObjectiveNodes,
      objectiveOutlineNodes,
      recommendedObjectiveNode,
    } = this.state;
    const { nodeTreeEditorContainerStyle } = theme;

    return (
      <div
        className={classes.container}
        id={"COACHMARKS_SCOPE_SEQUENCE_EDITOR"}
        style={nodeTreeEditorContainerStyle}
      >
        {mode == "edit" && (
          <CoachMarks
            type={"SCOPE_SEQUENCE_EDITOR"}
            onStepChanged={this.onStepChanged}
          />
        )}
        {(_.isNull(showOutline)
          ? uptoDepth > 1 || !uptoDepth
          : showOutline) && (
          <OutLine
            nodes={
              mode == "view"
                ? shouldShowSideNav
                  ? objectiveOutlineNodes
                  : nodes
                : editNodes
            }
            selectedNodeId={
              shouldShowSideNav ? sideNavSelectedNodeId : selectedNodeId
            }
            onChangeOutline={this.onChangeOutline}
            updateOutlineRef={this.updateOutlineRef}
            updateOutlineScrollRef={this.updateOutlineScrollRef}
            mode={mode}
            theme={theme}
            startDepth={startDepth}
            taggedNodeIds={taggedNodeIds}
            showSelectedCount={showSelectedCount}
            isDsComponent={isDsComponent || shouldShowSideNav}
            shouldShowSideNavWithoutTitle={shouldShowSideNav}
          />
        )}
        {/* {shouldShowSideNav && <SideNavigation
          options={getSideNavigationOptions({ parentNodes })}
          selectedOptionKey={sideNavigationTab}
          containerStyle={styles.sideNavigationContainerStyle}
          onClick={handleSideNavigationClick}
          testId={testId}
        />} */}
        {!isDsComponent && !shouldShowSideNav ? (
          <NodeTreeView
            nodes={mode == "view" ? filteredNodes : editNodes}
            filters={filters}
            mode={mode}
            startDepth={startDepth}
            isTagging={isTagging}
            showInsights={showInsights}
            insightsData={insightsData}
            onFilterChanged={this.onFilterChanged}
            taggedNodeIds={taggedNodeIds}
            onCheckBoxClick={this.onCheckBoxClick}
            updateGradeFilters={updateGradeFilters}
            gradeFilters={gradeFilters}
            rootOptionsGrades={rootOptionsGrades}
            updateEditNodeList={updateEditNodeList}
            rootNode={mode == "view" ? rootNode : editRootNode}
            onActionClick={this.onActionClick}
            updateScrollContainerRef={this.updateScrollContainerRef}
            updateNodesRef={this.updateNodesRef}
            handleScroll={this.handleScroll}
            isCoachMarksRunning={isCoachMarksRunning}
            theme={theme}
            uptoDepth={uptoDepth}
            value={value}
            showSearchHeader={showSearchHeader}
            multiSelectConfig={multiSelectConfig}
            updateTaggedNodes={this.updateTaggedNodes}
            nodeRenderType={nodeRenderType}
            showOutline={showOutline}
            searchPlaceHolder={this.getSearchPlaceHolder()}
            nodeType={nodeType}
            showGrade={showGrade}
            showParentInsightsCount={showParentInsightsCount}
            searchText={this.props.searchText}
            openedFrom={openedFrom}
            updateIsDisabled={updateIsDisabled}
            recommendedObjectiveNode={recommendedObjectiveNode}
            showGradeFilterExplicitly={showGradeFilterExplicitly}
            testId={testId}
          />
        ) : (
          <NodeTreeViewV2
            nodes={
              mode == "view"
                ? shouldShowSideNav
                  ? filteredObjectiveNodes
                  : filteredNodes
                : editNodes
            }
            filters={filters}
            mode={mode}
            startDepth={startDepth}
            isTagging={isTagging}
            showInsights={showInsights}
            insightsData={insightsData}
            onFilterChanged={this.onFilterChanged}
            taggedNodeIds={taggedNodeIds}
            onCheckBoxClick={this.onCheckBoxClick}
            updateGradeFilters={updateGradeFilters}
            gradeFilters={gradeFilters}
            rootOptionsGrades={rootOptionsGrades}
            updateEditNodeList={updateEditNodeList}
            rootNode={mode == "view" ? rootNode : editRootNode}
            onActionClick={this.onActionClick}
            updateScrollContainerRef={this.updateScrollContainerRef}
            updateNodesRef={this.updateNodesRef}
            handleScroll={this.handleScroll}
            isCoachMarksRunning={isCoachMarksRunning}
            theme={theme}
            uptoDepth={uptoDepth}
            value={value}
            showSearchHeader={showSearchHeader}
            multiSelectConfig={multiSelectConfig}
            updateTaggedNodes={this.updateTaggedNodes}
            nodeRenderType={nodeRenderType}
            showOutline={showOutline}
            searchPlaceHolder={this.getSearchPlaceHolder()}
            nodeType={nodeType}
            showGrade={showGrade}
            showParentInsightsCount={showParentInsightsCount}
            recommendedObjectiveNode={recommendedObjectiveNode}
            dsVersion={dsVersion}
            testId={testId}
          />
        )}

        {isLoading && <FullScreenLoader />}
        {!!currentDialogAction && (
          <DialogueBox
            modalTitle={DIALOGS_INFO[currentDialogAction].title(t)}
            showModal={true}
            onClickButton2={this.onDialogConfirmClick}
            modalBody={DIALOGS_INFO[currentDialogAction].message(t)}
            toggleDialogueBoxDisplay={this.OnToggleDialogueClick}
            button1={DIALOGS_INFO[currentDialogAction].cancelButtonText(t)}
            button2={DIALOGS_INFO[currentDialogAction].confirmButtonText(t)}
          />
        )}
      </div>
    );
  }
}

const orderBenchmarksMemoize = _.memoize(
  params => orderNodes(params),
  params => JSON.stringify(params)
);

const nodeFilterByGradesMemoize = _.memoize(
  params => nodeFilterByGrades(params),
  params => JSON.stringify(params)
);

const buildSearchFlatTreeMemoize = _.memoize(
  params => buildSearchFlatTree(params),
  params => JSON.stringify(params)
);

const mapActionCreators = {
  updateSearchFilters,
  initNodeList,
  updateEditNodeList,
  initRootNode,
  handleBenchmarkInBulk,
};

const mapStateToProps = (state, ownProps) => {
  const {
    nodes,
    gradeFilters = [],
    mode,
    rootNode,
    nodeType,
    startDepth,
    uptoDepth,
    shouldFilterNodesByGrades,
    excludeNodesWithExcludedChildren,
  } = ownProps;

  const filters = state.multiLevelNodeEditor.searchFilters;
  const searchTreeNodes = buildSearchFlatTreeMemoize({
    nodes: nodes,
    searchText: mode == "edit" ? "" : filters.searchText,
  });

  const orderedNodes = orderBenchmarksMemoize({
    nodes: searchTreeNodes,
    startDepth: !_.isNull(startDepth)
      ? startDepth
      : nodeType == "BENCHMARK"
      ? 0
      : 1,
    gradeSequences: _.get(rootNode, "grades", []),
  });

  let gradeFilterNodes = orderedNodes;
  if (mode == "view" && gradeFilters.length > 0 && shouldFilterNodesByGrades) {
    gradeFilterNodes = nodeFilterByGradesMemoize({
      nodes: orderedNodes,
      grades: mode == "view" ? gradeFilters : [],
    });
  }

  let filteredNodes = gradeFilterNodes;
  if (excludeNodesWithExcludedChildren) {
    filteredNodes = _.filter(gradeFilterNodes, node => {
      const isLeaf = _.get(node, "isLeaf", false);
      const depth = _.get(node, "depth", 0);
      return (
        isLeaf || depth == uptoDepth || _.get(node, "children", []).length > 0
      );
    });
  }
  return {
    nodes: filteredNodes,
    filteredNodes,
    editNodes: state.multiLevelNodeEditor.editNodes,
    isLoading: state.multiLevelNodeEditor.isLoading,
    editRootNode: state.multiLevelNodeEditor.rootNode,
    filters,
  };
};

export default compose(
  I18nHOC({ resource: ["common", "academicSetup"] }),
  connect(mapStateToProps, mapActionCreators)
)(NodeTreeEditor);
